import { selectView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useTransfersDrawer } from '@src/hooks';
import { appNotificationsSliceActions } from '@src/providers/AppNotifications/redux/AppNotificationsSlice';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  ButtonVariants,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  IndicatorBadgeVariants,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useMixpanel,
} from '@talos/kyoko';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback } from 'react';
import { HeaderButton } from './HeaderButton';

const { openInbox, closeInbox } = appNotificationsSliceActions;

export function AppInboxHeaderButton({ compact }: { compact: boolean }) {
  const mixpanel = useMixpanel();
  const view = useAppStateSelector(selectView);
  const dispatch = useAppStateDispatch();
  const { closeTransfersDrawer } = useTransfersDrawer();
  const appNotificationsState = useAppStateSelector(state => state.appNotifications);
  const activeCount = appNotificationsState.isReady
    ? Object.values(appNotificationsState.notificationMap).filter(toast => toast && toast.state !== 'closed').length
    : undefined;

  const handleClickInbox = useCallback(() => {
    closeTransfersDrawer(MixpanelEventSource.Header);
    const shouldClose = view === OMSView.NotificationsInbox;
    mixpanel.track(MixpanelEvent.ToggleShowNotificationsInbox, { [MixpanelEventProperty.Expanded]: !shouldClose });

    if (shouldClose) {
      dispatch(closeInbox());
      return;
    }
    dispatch(openInbox());
  }, [closeTransfersDrawer, dispatch, mixpanel, view]);

  return (
    <>
      <HeaderButton
        borderPlacement="left"
        onClick={handleClickInbox}
        size={FormControlSizes.Default}
        variant={view === OMSView.NotificationsInbox ? ButtonVariants.Priority : ButtonVariants.Default}
        data-testid="header-button-app-inbox"
        tooltip="Notifications Inbox"
        compact={compact}
        dim
      >
        <HStack gap="spacing4">
          <Icon icon={IconName.Inbox} />
          <AnimatePresence>
            {activeCount != null && activeCount > 0 && (
              <motion.div
                initial={{ width: 0, opacity: 0, scaleY: 0 }}
                animate={{ width: 'auto', opacity: 1, scaleY: 1 }}
                exit={{ width: 0, opacity: 0, scaleY: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                style={{ overflow: 'hidden', originY: 0.5 }}
              >
                <IndicatorBadge
                  children={activeCount}
                  variant={IndicatorBadgeVariants.Notification}
                  size={IndicatorBadgeSizes.Small}
                  data-testid="app-inbox-count-badge"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </HStack>
      </HeaderButton>
    </>
  );
}
